import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/api";
import { Link } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, totals, key } = useCart();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#FDFBF7] border-l border-[#EBE3D5] flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="border-b border-[#EBE3D5] pb-4">
          <SheetTitle className="font-serif-display text-3xl text-[#2C1E16] font-medium">
            Your Cart
            <span className="text-[#7B6F66] text-sm ml-3 font-sans-body font-normal">
              {totals.count} item{totals.count !== 1 && "s"}
            </span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-6 space-y-6">
          {items.length === 0 && (
            <div className="text-center py-20">
              <p className="font-serif-display text-2xl text-[#7B6F66]">Your cart is empty</p>
              <Link
                to="/shop"
                onClick={() => setOpen(false)}
                className="inline-block mt-6 text-xs uppercase tracking-[0.25em] link-underline text-[#1C3326]"
                data-testid="cart-empty-shop-link"
              >
                Browse the shop
              </Link>
            </div>
          )}
          {items.map((it) => {
            const k = key(it);
            return (
              <div key={k} className="flex gap-4" data-testid={`cart-line-${it.slug}`}>
                <img
                  src={it.image}
                  alt={it.name}
                  className="w-20 h-24 object-cover bg-[#F4EFE6]"
                />
                <div className="flex-1">
                  <div className="flex justify-between gap-2">
                    <div>
                      <div className="font-serif-display text-lg leading-tight">{it.name}</div>
                      {it.variant && (
                        <div className="text-xs uppercase tracking-[0.15em] text-[#7B6F66] mt-1">
                          {it.variant}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => removeItem(k)}
                      className="text-[#7B6F66] hover:text-[#C85A32]"
                      data-testid={`cart-remove-${it.slug}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="flex justify-between items-center mt-3">
                    <div className="flex items-center border border-[#EBE3D5]">
                      <button
                        onClick={() => updateQty(k, it.quantity - 1)}
                        className="p-2 hover:bg-[#F4EFE6]"
                        data-testid={`cart-dec-${it.slug}`}
                      >
                        <Minus size={12} />
                      </button>
                      <span className="px-3 text-sm">{it.quantity}</span>
                      <button
                        onClick={() => updateQty(k, it.quantity + 1)}
                        className="p-2 hover:bg-[#F4EFE6]"
                        data-testid={`cart-inc-${it.slug}`}
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    <div className="font-medium">{inr(it.price * it.quantity)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#EBE3D5] pt-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-[#7B6F66]">Subtotal</span>
              <span data-testid="cart-subtotal">{inr(totals.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#7B6F66]">Shipping</span>
              <span>{totals.shipping === 0 ? "Free" : inr(totals.shipping)}</span>
            </div>
            <div className="flex justify-between text-lg font-serif-display border-t border-[#EBE3D5] pt-3">
              <span>Total</span>
              <span data-testid="cart-total">{inr(totals.total)}</span>
            </div>
            <Link
              to="/checkout"
              onClick={() => setOpen(false)}
              data-testid="cart-checkout-button"
              className="block w-full text-center bg-[#1C3326] text-[#FDFBF7] hover:bg-[#15261C] py-4 uppercase text-xs tracking-[0.25em] font-medium transition-colors mt-4"
            >
              Proceed to Checkout
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
